import React from "react";
import { Link } from "react-router-dom";
import Button from "@mui/material/Button";
import GetAppIcon from "@mui/icons-material/GetApp";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import FlagIcon from "@mui/icons-material/Flag";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import PlaceIcon from "@mui/icons-material/Place";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import { fmt, rangeLabel, tagLabel, weightColors, glyph, ARC_ROLE, ARC_STATUS } from "../scenes/sceneModel";
import { Thumb } from "../scenes/SceneCard";
import { formatLabel, weightLabel, initials } from "./overviewModel";

// Links go to the Scenes tab, filtered (see Scenes.js: ?tag=, ?arc=, ?who=)
const scenesLink = (key, value) => "scenes?" + key + "=" + encodeURIComponent(value);
const face = (folder, track) => ({ backgroundImage: "url('" + folder + "/tracks_thumbnails/" + track + ".jpg')" });
const plural = (t, n, one, many) => (n === 1 ? t(one) : t(many, { count: n }));
// Enter or Space on a role="button" element
const onActivate = (fn) => (e) => {
  if (e.key !== "Enter" && e.key !== " ") return;
  e.preventDefault();
  fn();
};

/** the important characters, with their screen and speech times */
export function CastCard({ t, cast, folder, onReport }) {
  return (
    <section className="ov-card">
      <div className="ov-head">
        <h2 className="ov-h2">{t("Important Characters")}</h2>
        <span className="ov-count">{cast.length}</span>
        <span className="ov-legend">
          <span>
            <VisibilityIcon className="ov-ic ov-ic-screen" />
            {t("on screen")}
          </span>
          <span>
            <RecordVoiceOverIcon className="ov-ic" />
            {t("speaking")}
          </span>
        </span>
        <Button className="ov-report" variant="outlined" size="small" startIcon={<GetAppIcon />} onClick={onReport}>
          {t("Cast report")}
        </Button>
      </div>
      {cast.length > 0 && (
        <ul className="ov-cast">
          {cast.map((c) => (
            <li key={c.id} className="ov-castrow">
              <span className="ov-face" style={face(folder, c.track)} />
              <div className="ov-castbody">
                <div className="ov-castline">
                  <Link className="ov-name" to={scenesLink("who", c.name)} title={t("Show this character")}>
                    {c.name}
                  </Link>
                  <span className="ov-times" title={t("{{time}} on screen", { time: fmt(c.screen) }) + " · " + t("{{time}} speech", { time: fmt(c.speech) })}>
                    <span className="ov-screen">
                      <VisibilityIcon className="ov-ic ov-ic-screen" />
                      {fmt(c.screen)}
                    </span>
                    <span className="ov-speech">
                      <RecordVoiceOverIcon className="ov-ic" />
                      {fmt(c.speech)}
                    </span>
                  </span>
                </div>
                {c.role && <span className="ov-role">{c.role}</span>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** format and genre, content tags, logline, topics, settings and the full synopsis; parts the video lacks are left out */
export function SummaryCard({ t, summary, tags, hasScenes }) {
  const kind = summary ? [summary.format && t(formatLabel(summary.format)), summary.genre].filter(Boolean).join(" · ") : "";
  return (
    <section className="ov-card">
      {kind && <div className="sc-overline">{kind}</div>}
      {hasScenes && (
        <div className="ov-meta" title={t("Content")}>
          <FlagIcon className="ov-ic" />
          {tags.length > 0 ? (
            <span className="ov-tags">
              {tags.map((g) => (
                <Link key={g.tag} className="sc-badge sc-tag ov-taglink" to={scenesLink("tag", g.tag)} title={t("Show these scenes")}>
                  {t(tagLabel(g.tag))} · {plural(t, g.count, "1 scene", "{{count}} scenes")}
                </Link>
              ))}
            </span>
          ) : (
            <span>{t("No content flagged")}</span>
          )}
        </div>
      )}
      {summary && summary.logline && <p className="ov-logline">{summary.logline}</p>}
      {summary && summary.topics.length > 0 && (
        <div className="ov-meta" title={t("Topics")}>
          <LocalOfferIcon className="ov-ic" />
          <span>{summary.topics.join(" · ")}</span>
        </div>
      )}
      {summary && summary.settings.length > 0 && (
        <div className="ov-meta" title={t("Places")}>
          <PlaceIcon className="ov-ic" />
          <span>{summary.settings.join(" · ")}</span>
        </div>
      )}
      {summary && summary.synopsis && <p className="sc-prose ov-synopsis">{summary.synopsis}</p>}
    </section>
  );
}

/** the key moments; selecting one plays it, and the one playing is highlighted */
export function KeyMoments({ t, moments, playing, projectId, folder, onPlay }) {
  return (
    <>
      <div className="ov-label">
        <span className="sc-overline">{t("Key moments")}</span>
        <span className="ov-count">{plural(t, moments.length, "1 moment", "{{count}} moments")}</span>
      </div>
      <div className="ov-moments">
        {moments.map((m) => (
          <div key={m.k} className="ov-moment" role="button" tabIndex={0} aria-current={playing === m.k} onClick={() => onPlay(m.k)} onKeyDown={onActivate(() => onPlay(m.k))}>
            {m.scene ? <Thumb t={t} projectId={projectId} folder={folder} x={m.scene} /> : <span className="sc-thumb ov-nothumb" />}
            <span className="ov-mtext">
              <span className="ov-mtime">
                {m.scene && t("Scene") + " " + (m.scene.i + 1) + " · "}
                {rangeLabel(m.s, m.e)}
              </span>
              <span className="ov-mnote">{m.note}</span>
            </span>
            <span className="ov-mside">
              {m.faces.length > 0 && (
                <span className="ov-faces" role="img" aria-label={m.faces.map((f) => f.name).join(", ")}>
                  {m.faces.map((f) =>
                    f.track !== null ? (
                      <span key={f.name} className="ov-mface" title={f.name} style={face(folder, f.track)} />
                    ) : (
                      <span key={f.name} className="ov-mface" title={f.name}>
                        {initials(f.name)}
                      </span>
                    )
                  )}
                </span>
              )}
              <span className="ov-len" title={t("Length")} aria-label={t("{{time}} long", { time: fmt(m.e - m.s) })}>
                {fmt(m.e - m.s)}
              </span>
            </span>
          </div>
        ))}
      </div>
    </>
  );
}

/** a story step's marker, as on the Scenes tab's story map; glyphs are 6–10px, a 10px slot keeps the labels lined up */
function Glyph({ role }) {
  const g = glyph(role);
  return (
    <span className="ov-glyphslot">
      <span style={{ width: g.size + "px", height: g.size + "px", background: g.bg, border: g.border, borderRadius: g.radius, transform: "rotate(" + g.rotate + ")" }} />
    </span>
  );
}

/**
 * The story arcs, main plot first. One at a time opens in place: its summary and its steps, which play in the
 * player (the step in the scene playing is highlighted), and a link to the arc in the Scenes tab.
 * @param open index of the open arc, or null; detail: arcDetail(model, open)
 * @param sceneNow index of the scene playing, -1 before the video starts
 */
export function ArcList({ t, arcs, open, detail, sceneNow, onToggle, onStep }) {
  return (
    <section className="ov-card ov-arcsCard">
      <div className="ov-head">
        <h2 className="ov-h2">{t("Story arcs")}</h2>
        <span className="ov-count">{plural(t, arcs.length, "1 arc", "{{count}} arcs")}</span>
      </div>
      <div className="ov-arcs">
        {arcs.map((a) => {
          const [bg, fg] = weightColors(a.weight);
          const status = ARC_STATUS[a.status];
          const isOpen = open === a.k && !!detail;
          const toggle = () => onToggle(a.k);
          return (
            <div key={a.k} className="ov-arcitem" data-open={isOpen}>
              <div className="ov-arc" role="button" tabIndex={0} aria-expanded={isOpen} onClick={toggle} onKeyDown={onActivate(toggle)}>
                <span className="ov-weight" style={{ background: bg, color: fg }}>
                  {a.label}
                </span>
                <span className="ov-arcbody">
                  <span className="ov-arctitle">{a.title}</span>
                  <span className="ov-arcmeta">
                    {t(weightLabel(a.weight))}
                    {" · "}
                    {plural(t, a.steps.length, "1 scene", "{{count}} scenes")}
                    {status && (
                      <>
                        {" · "}
                        <span data-status={a.status}>{t(status)}</span>
                      </>
                    )}
                  </span>
                </span>
                {isOpen ? <ExpandLessIcon className="ov-chev" /> : <ExpandMoreIcon className="ov-chev" />}
              </div>
              {isOpen && (
                <div className="ov-arcopen">
                  {detail.summary && <p className="sc-small ov-arcsummary">{detail.summary}</p>}
                  <div className="ov-steps">
                    {detail.steps.map((q) => {
                      const play = () => onStep(q.t);
                      return (
                        <div
                          key={q.i}
                          className="sc-steprow ov-step"
                          role="button"
                          tabIndex={0}
                          aria-current={sceneNow === q.i}
                          title={t("Play from here")}
                          onClick={play}
                          onKeyDown={onActivate(play)}
                        >
                          <span className="ov-stepleft">
                            <span className="ov-steprole">
                              <Glyph role={q.role} />
                              {t(ARC_ROLE[q.role] || q.role)}
                            </span>
                            <span className="sc-time">
                              {t("Scene")} {q.i + 1} · {fmt(q.t)}
                            </span>
                          </span>
                          <span className="sc-small">
                            {q.headline && (
                              <>
                                <b>{q.headline}</b> —{" "}
                              </>
                            )}
                            {q.note}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <Link className="sc-link ov-openscenes" to={scenesLink("arc", a.k)}>
                    {t("Open in Scenes")}
                    <ChevronRightIcon />
                  </Link>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
